import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { useLoginForm } from '@features/auth/hooks/useLoginForm'
import { TwoFactorCells } from '@features/auth/components/TwoFactorCells'
import { AuthBrand } from '@features/auth/components/AuthBrand'
import {
  clearRememberedEmail,
  readRememberedEmail,
  writeRememberedEmail,
} from '@features/auth/services/rememberedEmail'
import { HighlightEmails } from '@shared/ui/HighlightEmails'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * #login 画面。T-C1 で `design/js/screens-auth.jsx` の Login を移植し、
 * AuthLayout 直下に置ける split-screen レイアウトに再構築した版。
 *
 * 設計判断:
 *   - state machine / onSubmit / API 呼出は `useLoginForm` フックに集約 (UI/ロジック分離)
 *   - 表示文言は i18n 経由 (`t('auth.*')`)。文言の直書きは禁止 (CLAUDE.md §2.10)
 *   - ログイン失敗時は inline error に加えて danger Toast を 1 度発火 (T-B5 の初消費)
 *   - 認証済表示は同じ split-screen 内で右ペインのみ差し替え (アート側を保持してブランド継続)
 *   - placeholder の dead-end UI とは独立した別ファイル (L-6 部分解消)
 *   - サブルート遷移 (`#register` / `#forgot` / `#terms` / `#privacy`) は素の `<a href="#...">`。
 *     ハッシュルーター (`useHashRoute`) が `hashchange` を listen するため JS handler 不要
 *   - フォーム要素には `useId` 由来の id を `htmlFor` で結ぶ (a11y / `getByLabelText` 対応)
 *
 * Phase 4 #44 Phase B (2FA login):
 *   - login 応答が `twoFactorRequired:true` のとき、右ペインを 2FA コード入力フォームに
 *     差し替える。submit で `useLoginForm.submitTwoFactorCode(code)` を呼ぶ。
 *   - キャンセル時は `cancelTwoFactor()` を呼び、idle に戻る (`_smkEnc` は hook 側で zeroize)。
 */

const LOGIN_TITLE_ID = 'login-title'

const ArtPanel = ({ withStats = true }: { withStats?: boolean }): JSX.Element => {
  const { t } = useTranslation()

  return (
    <div className="auth-art">
      <div className="grid-bg" />
      <div className="auth-art-content">
        <div className="auth-wordmark">OoKey</div>
        <h2>{t('auth.login.art_title')}</h2>
        {withStats && (
          <>
            <p>{t('auth.login.art_body')}</p>
            <div className="auth-art-stats">
              <div className="auth-art-stat">
                <div className="v">256</div>
                <div className="l">{t('auth.login.stat_aes')}</div>
              </div>
              <div className="auth-art-stat">
                <div className="v">99.9%</div>
                <div className="l">{t('auth.login.stat_uptime')}</div>
              </div>
              <div className="auth-art-stat">
                <div className="v">0</div>
                <div className="l">{t('auth.login.stat_breach')}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export const LoginPage = (): JSX.Element => {
  const { t } = useTranslation()

  const {
    state,
    isSubmitting,
    onSubmit,
    submitTwoFactorCode,
    requestTwoFactorEmailRecovery,
    submitTwoFactorEmailRecovery,
    cancelTwoFactor,
    lock,
    resendStatus,
    resendVerification,
    resendCooldownSec,
  } = useLoginForm()

  const toast = useToast()
  const emailId = useId()
  const passwordId = useId()
  const trustId = useId()
  // 「ログイン状態を保持」チェック時に保存した email を起動時に 1 度だけ読む。
  // useState の initializer なので mount 後の再 render では再評価されない (defaultValue 用途)。
  const [initialEmail] = useState<string>(() => readRememberedEmail() ?? '')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  // 2FA 入力モード:
  //   'totp'     — Authenticator app の 6 桁 (default)
  //   'recovery' — recovery code (xxxx-xxxx-xx)
  //   'email'    — メール経由 6 桁 一時コード (Authenticator + recovery code 喪失時)
  const [twoFactorMode, setTwoFactorMode] = useState<'totp' | 'recovery' | 'email'>('totp')
  // email モードでメール送信済か (true なら code 入力 UI を表示)
  const [emailRecoverySent, setEmailRecoverySent] = useState(false)
  const twoFactorFormRef = useRef<HTMLFormElement>(null)

  // login form の validity 追跡: email/password 両方入力済 + email 形式 OK で submit 活性化。
  // noValidate な form でも input 単位の checkValidity (required + type=email) は機能する。
  // onInput 経由で formRef.current?.checkValidity() を呼び結果を state に反映する。
  const loginFormRef = useRef<HTMLFormElement>(null)
  const [isLoginFormValid, setIsLoginFormValid] = useState(false)

  const recomputeLoginFormValidity = (): void => {
    const f = loginFormRef.current
    if (!f) return
    // HTMLFormElement.checkValidity() — required + type=email を満たせば true
    setIsLoginFormValid(f.checkValidity())
  }

  // ログイン失敗時に danger Toast を発火 (state object の identity が新しい場合のみ)。
  // toast.show は `useCallback([])` で stable、useToast() value は useMemo で stable のため、
  // 依存配列に含めても無限ループにはならない (R-6: ToastProvider.tsx の stability 契約に依存)。
  // `dedupe: true` で同 messageKey の連続表示を抑止 (R-5 / 旧 L-19): 連続 401 等で
  // 同じエラーが Toast stack に何度も積まれる UX 問題を防ぐ。
  // **認証成功時は出していた error Toast を dismiss** して、次画面 (#home) に古いエラーを
  // 持ち越さない (ユーザーは「成功したのに赤いエラーが残っている」誤認を起こしやすい)。
  const errorToastIdsRef = useRef<string[]>([])

  useEffect(() => {
    if (state.kind === 'error') {
      errorToastIdsRef.current.push(
        toast.show({ tone: 'danger', titleKey: state.messageKey, dedupe: true }),
      )

      // 410 (account_deleted) は再試行不可なので password だけ即クリア
      // (email は保持して「どのアカウントが該当か」をユーザーが認識できる UX)。
      // submit ボタンも password 空 → checkValidity() false で自動 disabled。
      if (state.messageKey === 'auth.error.account_deleted') {
        const form = loginFormRef.current

        if (form) {
          const pw = form.elements.namedItem('password')

          if (pw instanceof HTMLInputElement) pw.value = ''
          setIsLoginFormValid(form.checkValidity())
        }
      }
    }
    if (state.kind === 'authenticated') {
      errorToastIdsRef.current.forEach(id => toast.dismiss(id))
      errorToastIdsRef.current = []
      window.location.hash = 'home'
    }
    if (state.kind === 'idle') {
      setTwoFactorCode('')
      setTwoFactorMode('totp')
      setEmailRecoverySent(false)
    }
    // 2FA エラー時もコード入力を初期化し、ユーザーに即座に再入力を促す。
    // TwoFactorCells 側の code='' useEffect で先頭セルへ自動 focus される。
    // danger Toast で UI 上端にも通知 (inline error は cells 直下にも残る)。
    if (state.kind === 'two_factor_error') {
      setTwoFactorCode('')
      errorToastIdsRef.current.push(
        toast.show({ tone: 'danger', titleKey: state.messageKey, dedupe: true }),
      )
    }
  }, [state, toast])

  if (state.kind === 'authenticated') {
    return (
      <div className="auth-stage">
        <ArtPanel withStats={false} />
        <div className="auth-form">
          <div className="auth-form-inner">
            <h1>{t('auth.title_authenticated')}</h1>
            <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={lock}>
              {t('auth.lock_button')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (
    state.kind === 'two_factor_required' ||
    state.kind === 'two_factor_submitting' ||
    state.kind === 'two_factor_error'
  ) {
    const handleTwoFactorSubmit = (ev: FormEvent<HTMLFormElement>): void => {
      ev.preventDefault()

      if (twoFactorMode === 'email') {
        // email mode: 受信した 6 桁 code を verify
        void submitTwoFactorEmailRecovery(twoFactorCode.trim())

        return
      }

      // recovery mode の入力はダッシュを含むので、submit 直前に空白だけ trim する
      // (大文字小文字 / dash 有無は backend hashRecoveryCode で normalize される)。
      const code = twoFactorMode === 'recovery' ? twoFactorCode.trim() : twoFactorCode

      void submitTwoFactorCode(code)
    }

    // TOTP は 6 桁固定、Recovery code は backend DTO で 10–14 chars (hex + dash)、
    // email recovery は 6 桁数字。
    const isComplete =
      twoFactorMode === 'recovery'
        ? twoFactorCode.replace(/\s/gu, '').length >= 10
        : twoFactorCode.length === 6

    const hasError = state.kind === 'two_factor_error'

    const handleRequestEmailRecovery = async (): Promise<void> => {
      if (isSubmitting) return
      // optimistic UI: show input first (server returns 204 regardless of cooldown)
      setEmailRecoverySent(true)
      setTwoFactorCode('')
      try {
        await requestTwoFactorEmailRecovery()
      } catch {
        // useLoginForm 側で error state を立てるので UI は自動更新される
      }
    }

    return (
      <div className="auth-stage">
        <ArtPanel withStats={false} />
        <div className="auth-form">
          <div className="auth-form-inner auth-twofa-pane">
            <div className="auth-twofa-hero" aria-hidden="true">
              <span className="auth-twofa-hero-circle">
                <Icon name="shield" />
              </span>
            </div>
            <div className="auth-twofa-hero-text">
              <h1 className="auth-twofa-title">{t('auth.two_factor.title')}</h1>
            </div>

            {/* 認証方法切替 — 3 つを 1 行の segmented pill にまとめる。
                現在モードがハイライト、ほかをクリックで即切替 (入力はクリア)。 */}
            <div
              className="seg-group auth-twofa-mode-seg"
              role="radiogroup"
              aria-label={t('auth.two_factor.method_label')}
            >
              {(['totp', 'recovery', 'email'] as const).map(m => {
                const active = twoFactorMode === m

                const labelKey =
                  m === 'totp'
                    ? 'auth.two_factor.method_totp'
                    : m === 'recovery'
                      ? 'auth.two_factor.method_recovery'
                      : 'auth.two_factor.method_email'

                return (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`seg-btn${active ? ' seg-btn-active' : ''}`}
                    onClick={() => {
                      if (active) return
                      setTwoFactorMode(m)
                      setTwoFactorCode('')
                      setEmailRecoverySent(false)
                    }}
                    disabled={isSubmitting}
                    data-testid={`twofa-mode-${m}`}
                  >
                    {t(labelKey)}
                  </button>
                )
              })}
            </div>

            <p className="auth-twofa-helper">
              {twoFactorMode === 'totp' && t('auth.two_factor.intro')}
              {twoFactorMode === 'recovery' && t('auth.two_factor.intro_recovery')}
              {twoFactorMode === 'email' &&
                (emailRecoverySent
                  ? t('auth.two_factor.intro_email_sent')
                  : t('auth.two_factor.intro_email'))}
            </p>
            <form
              ref={twoFactorFormRef}
              className="auth-twofa-form"
              onSubmit={handleTwoFactorSubmit}
              noValidate
              data-testid="twofa-login-form"
            >
              {/* TOTP: 6 digit cells */}
              {twoFactorMode === 'totp' && (
                <TwoFactorCells
                  code={twoFactorCode}
                  onCodeChange={setTwoFactorCode}
                  onAutoSubmit={() => twoFactorFormRef.current?.requestSubmit()}
                  disabled={isSubmitting}
                  hasError={hasError}
                />
              )}

              {/* Recovery code: monospace text input */}
              {twoFactorMode === 'recovery' && (
                <input
                  type="text"
                  inputMode="text"
                  autoComplete="one-time-code"
                  autoCapitalize="off"
                  spellCheck={false}
                  className={`input lg auth-twofa-recovery-input${hasError ? ' input--invalid' : ''}`}
                  placeholder="xxxx-xxxx-xx"
                  value={twoFactorCode}
                  onChange={ev => setTwoFactorCode(ev.target.value)}
                  disabled={isSubmitting}
                  aria-invalid={hasError}
                  data-testid="twofa-recovery-input"
                />
              )}

              {/* Email recovery — Step 1: compact request button (centered + mail icon) */}
              {twoFactorMode === 'email' && !emailRecoverySent && (
                <div className="auth-twofa-email-cta">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => void handleRequestEmailRecovery()}
                    disabled={isSubmitting}
                    data-testid="twofa-email-request"
                  >
                    <Icon name="mail" aria-hidden="true" />
                    {t('auth.two_factor.email_send_button')}
                  </button>
                </div>
              )}
              {twoFactorMode === 'email' && emailRecoverySent && (
                <TwoFactorCells
                  code={twoFactorCode}
                  onCodeChange={setTwoFactorCode}
                  onAutoSubmit={() => twoFactorFormRef.current?.requestSubmit()}
                  disabled={isSubmitting}
                  hasError={hasError}
                />
              )}

              {hasError && (
                <p className="form-error auth-twofa-error" role="alert">
                  {t(state.messageKey)}
                </p>
              )}

              {/* email mode の Step 1 ではまだ submit ボタン不要 */}
              {!(twoFactorMode === 'email' && !emailRecoverySent) && (
                <button
                  type="submit"
                  className="btn btn-primary btn-lg btn-block auth-twofa-submit"
                  disabled={isSubmitting || !isComplete}
                >
                  {isSubmitting ? t('auth.two_factor.submitting') : t('auth.two_factor.submit')}
                </button>
              )}
            </form>

            <button
              type="button"
              className="btn btn-secondary btn-lg btn-block auth-twofa-cancel"
              onClick={cancelTwoFactor}
              disabled={isSubmitting}
            >
              {t('auth.two_factor.cancel')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-stage">
      <ArtPanel />
      <div className="auth-form">
        <div className="auth-form-inner">
          <AuthBrand />
          <h1 id={LOGIN_TITLE_ID}>{t('auth.title')}</h1>
          <form
            ref={loginFormRef}
            className="login-form"
            onSubmit={ev => {
              // 「ログイン状態を保持」(trustDevice) が ON のとき、入力された email を
              // localStorage に保存して次回起動時に prefill する。OFF のときは削除。
              // currentTarget は await を跨ぐと null 化されるので onSubmit より前で読む。
              const data = new FormData(ev.currentTarget)
              const email = String(data.get('email') ?? '').trim()
              const trustDevice = data.get('trustDevice') !== null
              if (trustDevice && email !== '') writeRememberedEmail(email)
              else clearRememberedEmail()
              onSubmit(ev)
            }}
            onInput={recomputeLoginFormValidity}
            noValidate
          >
            <div className="field">
              <label className="field-label" htmlFor={emailId}>
                {t('auth.field_email')}
              </label>
              <input
                id={emailId}
                name="email"
                type="email"
                className="input lg"
                autoComplete="username"
                defaultValue={initialEmail}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="field">
              <div className="field-label-row">
                <label className="field-label" htmlFor={passwordId}>
                  {t('auth.field_password')}
                </label>
                <a className="field-label-link" href="#forgot">
                  {t('auth.login.forgot_link')}
                </a>
              </div>
              <PasswordInput
                id={passwordId}
                name="password"
                size="lg"
                autoComplete="current-password"
                placeholder={t('auth.login.field_password_placeholder')}
                required
                disabled={isSubmitting}
              />
            </div>
            <label className="check-row" htmlFor={trustId}>
              <input id={trustId} name="trustDevice" type="checkbox" defaultChecked />
              <span>{t('auth.login.trust_device')}</span>
            </label>
            {state.kind === 'error' && (
              <p className="form-error" role="alert">
                <HighlightEmails text={t(state.messageKey)} />
              </p>
            )}
            {state.kind === 'email_unverified' && (
              <div className="login-verify-block" role="alert">
                <p className="form-error">{t('auth.error.email_not_verified')}</p>
                {resendStatus === 'sent' ? (
                  <p className="auth-prompt">{t('auth.login.resend_done')}</p>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => void resendVerification()}
                    disabled={resendStatus === 'sending' || resendCooldownSec > 0}
                    data-testid="login-resend-verification"
                  >
                    {resendStatus === 'sending'
                      ? t('auth.login.resend_sending')
                      : resendCooldownSec > 0
                        ? t('auth.login.resend_cooldown', { sec: resendCooldownSec })
                        : t('auth.login.resend_button')}
                  </button>
                )}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block"
              disabled={isSubmitting || !isLoginFormValid}
            >
              {isSubmitting ? t('auth.submitting') : t('auth.submit')}
            </button>
          </form>
          <p className="auth-prompt">
            {t('auth.login.signup_prompt')} <a href="#register">{t('auth.login.signup_link')}</a>
          </p>
          <div className="links">
            <a href="#terms">{t('auth.links.terms')}</a>
            <span className="separator" />
            <a href="#privacy">{t('auth.links.privacy')}</a>
          </div>
        </div>
      </div>
    </div>
  )
}
